export type QuoteSuggestionErrorKey =
  | 'daily_limit_reached'
  | 'transcript_unavailable'
  | 'not_found'
  | 'timeout'
  | 'generation_failed';

const APPROVED_CODES: ReadonlySet<string> = new Set([
  'daily_limit_reached',
  'transcript_unavailable',
  'not_found',
]);

/** The hook's own client-side timeout error message (never a backend code). */
const TIMEOUT_MESSAGE = 'quote_suggestion_timeout';

interface GraphQLErrorLike {
  message?: unknown;
}

interface ApolloErrorLike {
  message?: unknown;
  graphQLErrors?: unknown;
}

function extractMessage(error: unknown): string | null {
  if (typeof error === 'string') return error;
  if (error === null || typeof error !== 'object') return null;

  const { message, graphQLErrors } = error as ApolloErrorLike;

  if (Array.isArray(graphQLErrors) && graphQLErrors.length > 0) {
    const first = (graphQLErrors[0] as GraphQLErrorLike | undefined)?.message;
    if (typeof first === 'string') return first;
  }

  return typeof message === 'string' ? message : null;
}

/**
 * Maps an unknown thrown value (an Apollo `ApolloError`, a plain `Error`,
 * or a raw string) to a stable `QuoteSuggestionErrorKey`. A raw
 * provider/backend message never leaks through — anything unrecognized
 * becomes `'generation_failed'`.
 */
export function mapQuoteSuggestionError(error: unknown): QuoteSuggestionErrorKey {
  const message = extractMessage(error);
  if (message === TIMEOUT_MESSAGE) return 'timeout';
  if (message !== null && APPROVED_CODES.has(message)) {
    return message as QuoteSuggestionErrorKey;
  }
  return 'generation_failed';
}
